import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin, interval, merge, Observable, of, Subscription, throwError } from 'rxjs';
import cloneDeep from 'lodash-es/cloneDeep';
import uniq from 'lodash-es/uniq';
import { map, mergeMap, startWith, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { OrderStatus, OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { LoaderStatus } from 'libs/core/src/lib/model/loading/loading-status.enum';
import { CateringViewModel } from 'libs/core/src/lib/model/view-model/order/catering.view.model';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { VoucherTypeModel } from 'libs/core/src/lib/model/voucher-type.model';
import { CateringAggregationArticleViewModel, CateringAggregationViewModel } from 'libs/core/src/lib/model/catering/catering-aggregation.view.model';
import { ScreenScreeningLinkModel } from './model/screen-screening-link.model';
import { MobileDeviceUtils } from 'libs/core/src/lib/utilities/mobile-device-utils';
import { makeUrl } from 'libs/core/src/public-api';
import { FindByScreeningOptions } from 'libs/core/src/lib/data-provider/screen.data-provider';
import { SelectedModifierItemMap } from 'libs/core/src/lib/service/catering/order-catering.service';
import { storageKey } from 'libs/core/src/app.const';
import { SalesDocumentViewModel } from 'libs/core/src/lib/model/view-model/sales-document/sales-document.view.model';
import { OrderItemType, ScreeningItemViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item.view.model';
import { ScreeningItemExtraFeeViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item-extra-fee.view.model';
import { FbItemViewModel } from 'libs/core/src/lib/model/view-model/order/fb-item/fb-item.view.model';
import { FbItemSubArticleViewModel } from 'libs/core/src/lib/model/view-model/order/fb-item/fb-item-sub-article.view.model';
import { FbItemModifierItemViewModel } from 'libs/core/src/lib/model/view-model/order/fb-item/fb-item-modifier-item.view.model';
import { VoucherItemViewModel } from 'libs/core/src/lib/model/view-model/order/voucher-item/voucher-item.view.model';
import { CateringAggregationBuilder } from 'libs/core/src/lib/model/catering/catering-aggregation.builder';
import { CinemaViewModel } from 'libs/core/src/lib/model/view-model/cinema/cinema.view.model';
import { ScreenViewModel } from 'libs/core/src/lib/model/view-model/screen/screen.view.model';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { TicketViewModel } from 'libs/core/src/lib/model/view-model/shared/ticket/ticket.view.model';
import { paymentStatusTypes } from '../payment/worldpay-redirect-hub/payment-status-type.model';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FlowType } from 'libs/core/src/lib/model/component/steps/flow-type.model';
import { OrderApiModel } from 'libs/core/src/lib/model/api-model/order/order.api.model';
import { OrderSummaryBasePageComponent } from './order-summary-base.component';
import { NavigationService } from 'libs/core/src/lib/service/navigation/navigation.service';

@Component({
  template: ''
})
export class OrderSummaryPageComponent extends OrderSummaryBasePageComponent implements OnInit, OnDestroy {
  private orderSubscription: Subscription = Subscription.EMPTY;
  public cinemaId: string = null;
  public cinema: CinemaViewModel = null;
  public orderId: string = null;
  public salesDocumentId: string = null;
  public order: OrderViewModel = null;
  public screen: ScreenViewModel = null;
  public generatingVirtualPass = false;
  public tickets: Array<TicketViewModel> = null;
  public catering: CateringViewModel = null;
  public basketPageModel: BasketPageModel;
  public selectedVoucherList: Array<VoucherTypeModel> = null;
  public selectedCateringArticleCombinationList: Array<CateringAggregationArticleViewModel> = null;
  public voucherNumber: string | null = null;
  public isVoucherQrCodeZoom = false;
  public isQrCodeZoom: { [key: string]: boolean } = {};
  public screenMap: Array<ScreenScreeningLinkModel> = new Array<ScreenScreeningLinkModel>();
  private queryParamsSubscription: Subscription = Subscription.EMPTY;
  private maxTriesAmount = 6;
  private currentTry = 0;
  public readonly voucherSaleEnabled: boolean = false;
  public cateringSaleEnabled = false;
  public cateringAvailableSubscription: Subscription = Subscription.EMPTY;
  public childLoaderStatus: LoaderStatus;
  public showZoomIcon = !MobileDeviceUtils.isAndroid();
  public modalRef: BsModalRef;

  @ViewChild('modal') modal: TemplateRef<any>;

  public flowType: FlowType = null;
  public flowTypeEnum: typeof FlowType = FlowType;

  public userLoggedIn = false;
  public documentNumber: string;

  private orderExtractorTimeoutId: any;

  constructor(
    protected injector: Injector,
    protected navigationService: NavigationService
  ) {
    super(injector);
    this.voucherSaleEnabled = this.environment.constants.voucherSaleEnabled;
    this.flowType = this.flowService.FlowType;

    if (this.cateringAvailableSubscription === Subscription.EMPTY) {
      this.cateringAvailableSubscription = this.cateringStateService.state$.subscribe((value) => {
        this.cateringSaleEnabled = value;
      });
    }
  }

  public ngOnInit() {
    this.backgroundService.changeDeleteOrderOnWindowUnloadState(false);
    this.cateringStateService.checkAvailability();
    this.countdownComponentService.destroy();

    this.childLoaderStatus = LoaderStatus.pending;

    this.orderExtractorTimeoutId = setTimeout(() => {
      this.extractOrderData();
    }, 500);
  }

  ngAfterViewChecked() {
    this.titleService.setTitle(this.translationService.instant('order.orderSummary.pageTitle'));
  }

  private extractOrderData() {
    this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
      this.cinemaId = params['cinemaId'] ?? this.orderStateService.getItem(storageKey.chosenLocation);
      if (!this.cinemaId) {
        console.error('No cinemaId');
        return this.navigationHelper.goToRepertoire();
      }

      const orderId = params['orderId'];
      const salesDocumentId = params['salesDocumentId'];
      const hostedPaymentStatus = params['HostedPaymentStatus'];

      if ((!orderId && !salesDocumentId) || (hostedPaymentStatus && hostedPaymentStatus == paymentStatusTypes.cancel)) {
        return this.navigationHelper.goToRepertoire();
      }

      this.salesDocumentId = salesDocumentId;

      const cinemaId = this.cinemaId;
      forkJoin({
        order: this.orderStateService.getOrder$(cinemaId, orderId),
        cinema: this.cinemaDataProvider.getCinemaById(cinemaId)
      })
        .pipe(
          switchMap((data) => {
            this.setOrder(data.order);
            if (!data.order) {
              this.navigationHelper.goToRepertoire();
              return throwError(() => new Error('No order found'));
            }

            this.orderId = data.order.id;
            this.cinema = data.cinema;

            if (this.salesDocumentId) {
              return this.loadSalesDocument();
            }

            this.startOrderStatusPolling(data.order, (order) => {
              this.loadOrder(order);
            });

            return of(null);
          })
        )
        .subscribe();
    });
  }

  protected afterOrderSet() {}

  private setOrder(order) {
    this.order = order;
    this.documentNumber = order?.bookingId?.length ? order.bookingId : order?.documentNumber?.length ? order.documentNumber : null;
    this.afterOrderSet();
  }

  private buildOrderFromSalesDocument(salesDocument: SalesDocumentViewModel): OrderViewModel {
    const order = new OrderApiModel();
    order.bookingId = salesDocument.bookings[0]?.bookingId;
    order.status = OrderStatus.FINISHED;
    order.totalValue = salesDocument.totalValue;
    order.userFirstName = salesDocument.userFirstName;
    order.userLastName = salesDocument.userLastName;
    order.userPhone = salesDocument.userPhoneNumber;
    order.userEmail = salesDocument.userEmail;
    order.screeningItems = this.buildItemsModelFromSalesDocument(salesDocument);
    order.voucherItems = this.buildVoucherItemsModelFromSalesDocument(salesDocument);
    order.documentNumber = salesDocument.orderNumber;

    const result = new OrderViewModel(this.cinemaId, order);
    result.pickupTime = salesDocument.pickupTime;
    result.createdFromSalesDocument = true;
    result.fbItems = this.buildCateringItemsModelFromSalesDocument(salesDocument);
    return result;
  }

  private buildItemsModelFromSalesDocument(salesDocument: SalesDocumentViewModel): ScreeningItemViewModel[] {
    const items: ScreeningItemViewModel[] = Array<ScreeningItemViewModel>();

    for (const salesDocumentItem of salesDocument.reservationItems) {
      const item: ScreeningItemViewModel = new ScreeningItemViewModel();
      const optionalExtraFees: Array<string> = new Array<string>();

      item.seat = salesDocumentItem.seat;
      item.row = salesDocumentItem.row;
      item.name = salesDocumentItem.ticketName;
      item.screeningId = salesDocument.bookings[0]?.screeningId;
      item.bookingId = salesDocument.bookings[0]?.bookingId;
      item.price = salesDocumentItem.price;
      item.ticketPrice = salesDocumentItem.price;
      item.voucherName = salesDocumentItem.voucherName;
      item.voucherNumber = null;
      item.quantity = 1;

      for (const salesDocumentExtraFee of salesDocumentItem.extraFees) {
        const extraFee: ScreeningItemExtraFeeViewModel = new ScreeningItemExtraFeeViewModel();

        extraFee.name = salesDocumentExtraFee.name;
        extraFee.price = salesDocumentExtraFee.price;
        extraFee.isOptional = salesDocumentExtraFee.isOptional;

        if (extraFee.isOptional) {
          optionalExtraFees.push(salesDocumentExtraFee.name); // name is ID
        }

        item.extraFees.push(extraFee);
      }

      item.optionalExtraFees = optionalExtraFees;
      items.push(item);
    }

    return items;
  }

  private buildCateringItemsModelFromSalesDocument(salesDocument: SalesDocumentViewModel): FbItemViewModel[] {
    const items: FbItemViewModel[] = Array<FbItemViewModel>();

    for (const salesDocumentItem of salesDocument.items) {
      if (salesDocumentItem.itemType !== OrderItemType.FB_ITEM) {
        continue;
      }

      const item: FbItemViewModel = new FbItemViewModel();

      item.name = salesDocumentItem.name;
      item.price = salesDocumentItem.price;
      item.quantity = salesDocumentItem.quantity;
      item.voucherName = salesDocumentItem.voucherName;

      for (const salesDocumentSubItem of salesDocumentItem.subItems) {
        const subItem: FbItemSubArticleViewModel = new FbItemSubArticleViewModel();

        subItem.name = salesDocumentSubItem.name;
        subItem.price = salesDocumentSubItem.price;
        subItem.quantity = salesDocumentSubItem.quantity;

        item.subArticleList.push(subItem);
      }

      for (const salesDocumentModifier of salesDocumentItem.modifierItems) {
        const modifier: FbItemModifierItemViewModel = new FbItemModifierItemViewModel();

        modifier.modifierName = salesDocumentModifier.itemName;
        modifier.modifierItemDescription = salesDocumentModifier.name;
        modifier.modifierItemName = salesDocumentModifier.modifierItemName;
        modifier.quantity = salesDocumentModifier.quantity / salesDocumentItem.quantity;
        modifier.price = salesDocumentModifier.price;

        item.modifierItemList.push(modifier);
      }

      items.push(item);
    }

    return items;
  }

  private buildVoucherItemsModelFromSalesDocument(salesDocument: SalesDocumentViewModel): VoucherItemViewModel[] {
    const items: VoucherItemViewModel[] = Array<VoucherItemViewModel>();

    for (const salesDocumentItem of salesDocument.items) {
      if (salesDocumentItem.itemType !== OrderItemType.VOUCHER) {
        continue;
      }

      const item: VoucherItemViewModel = new VoucherItemViewModel();

      item.itemName = salesDocumentItem.name;
      item.value = salesDocumentItem.price;
      item.quantity = salesDocumentItem.quantity;

      items.push(item);
    }

    return items;
  }

  public ngOnDestroy(): void {
    if (this.queryParamsSubscription !== Subscription.EMPTY) {
      this.queryParamsSubscription.unsubscribe();
      this.queryParamsSubscription = Subscription.EMPTY;
    }

    if (this.cateringAvailableSubscription !== Subscription.EMPTY) {
      this.cateringAvailableSubscription.unsubscribe();
      this.cateringAvailableSubscription = Subscription.EMPTY;
    }

    if (this.orderExtractorTimeoutId) {
      clearTimeout(this.orderExtractorTimeoutId);
    }

    if (this.orderSubscription) {
      this.orderSubscription.unsubscribe();
    }
  }

  public onClickBackToHome(): void {
    window.location.href = this.environment.backUrl.replace('{lang}', this.translationService.currentLang);
  }

  orderStatusLoadingCompleted: boolean = false;
  private startOrderStatusPolling(initialOrder: OrderViewModel, completeCollbackFn?: (order: OrderViewModel) => void): void {
    this.loaderService.hideLoader(LoaderEnum.PAYMENT);

    const isFinalStatus = initialOrder.status === OrderStatus.FINISHED || initialOrder.status === OrderStatus.CANCELLED;
    if (isFinalStatus) {
      console.log('Polling not needed. Order already in a final state:', initialOrder.status);
      completeCollbackFn && completeCollbackFn(initialOrder);
      this.orderStatusLoadingCompleted = true;
      return;
    }

    console.log('Starting order status polling...');
    this.orderSubscription = interval(5000)
      .pipe(
        startWith(0),
        take(this.maxTriesAmount),
        switchMap(() => this.orderStateService.getOrder$(this.cinemaId, this.orderId)),
        tap((orderSimple) => console.log('Polling check. Status:', orderSimple.status)),
        takeWhile((orderSimple) => orderSimple.status !== OrderStatus.FINISHED && orderSimple.status !== OrderStatus.CANCELLED, true),
        tap((finalOrderState) => {
          completeCollbackFn && completeCollbackFn(finalOrderState);
        })
      )
      .subscribe({
        complete: () => {
          console.log('Polling finished.');
          this.orderStatusLoadingCompleted = true;
        }
      });
  }

  private loadSalesDocument() {
    return this.salesDocumentDataProvider.getSalesDocumentById(this.cinemaId, this.salesDocumentId).pipe(
      switchMap((salesDocument) => this.loadScreening(salesDocument)),
      tap((result) => {
        this.setOrder(this.buildOrderFromSalesDocument(result));
        this.loadVoucherItemList();
        this.screen = result.screen;
        const screenModel: ScreenViewModel = result.screen;
        const screenMap: ScreenScreeningLinkModel[] = new Array<ScreenScreeningLinkModel>();
        screenMap.push(new ScreenScreeningLinkModel(result.screen.id, result.bookings[0]?.bookingId, result.orderNumber, screenModel));
        this.screenMap = screenMap.map((x) => x);

        if (screenModel.posterUrl === undefined || screenModel.posterUrl === null || screenModel.posterUrl === '') {
          screenModel.posterUrl = makeUrl(this.environment, this.environment.constants.moviePosterPlaceholder);
        }

        this.screenStateService.setData(screenModel);

        const articleCombinationList: CateringAggregationArticleViewModel[] = new Array<CateringAggregationArticleViewModel>();
        for (const cateringItem of this.order.fbItems) {
          articleCombinationList.push(this.cateringService.buildSelectedArticleFromSalesDocument(cateringItem));
        }

        this.selectedCateringArticleCombinationList = articleCombinationList.map((x) => x);
      })
    );
  }

  private loadScreening(salesDocument: SalesDocumentViewModel) {
    console.log('loadScreening');
    return this.screeningDataProvider.findByIdViaApiModel(this.cinemaId, salesDocument.bookings[0]?.screeningId).pipe(
      mergeMap((movieCopy) => {
        // salesDocument.movieCopy = movieCopy.copy;
        return this.screenDataProvider.getScreenBy(this.cinemaId, movieCopy.screenings[0].screenId).pipe(
          map((screen) => {
            screen.movieDate = salesDocument.bookings[0]?.screeningDate;
            screen.movieName = salesDocument.movieCopy.movie.title;

            screen.id = salesDocument.movieCopy.screenings[0].id;
            screen.moviePremiere = salesDocument.movieCopy.movie.premiereDate;
            screen.movieRating = salesDocument.movieCopy.movie.ratings;
            screen.movieDuration = salesDocument.movieCopy.movie.duration;
            screen.movieLanguage = movieCopy.copy.language;
            screen.posterUrl = (movieCopy.movie.posters?.length ? movieCopy.movie.posters[0] : null) || '';
            screen.movieGenres = movieCopy.movie.genres || [];
            screen.movieTags = movieCopy.movie.tagGroups || [];
            screen.moviePrintType = movieCopy.copy.printType;
            screen.screeningDuration = salesDocument.movieCopy.screenings[0].screeningDuration;
            screen.isScreenSwapping = salesDocument.movieCopy.screenings[0].isScreenSwapping;
            screen.screeningAvailabilityStatus = salesDocument.movieCopy.screenings[0].availabilityStatus;
            screen.reservationTimeTo = salesDocument.movieCopy.screenings[0].reservationTimeTo;
            screen.saleTimeTo = salesDocument.movieCopy.screenings[0].saleTimeTo;
            screen.screeningTimeFrom = salesDocument.movieCopy.screenings[0].screeningTimeFrom;
            screen.screeningTimeTo = salesDocument.movieCopy.screenings[0].saleTimeTo;
            screen.movieSoundType = movieCopy.copy.soundType;
            screen.movieSubtitles = [movieCopy.copy.subtitles, movieCopy.copy.subtitles2].filter((value) => value !== null);
            screen.generalAdmission = salesDocument.movieCopy.screenings[0].generalAdmission;
            screen.movieDescription = movieCopy.movie.description;

            salesDocument.screen = screen;
            return salesDocument;
          })
        );
      })
    );
  }

  private loadOrder(order: OrderViewModel): void {
    this.setOrder(cloneDeep(order));
    this.googleTagManagerService.purchase();

    if (this.order.userPhone) {
      this.order.userPhone = this.order.userPhone
        .replace(/[^\dA-Z]/g, '')
        .replace(/(.{3})/g, '$1 ')
        .trim();
    }

    const screeningIdentifierCollection: Array<string> = uniq(order.screeningItems.map((x) => x.screeningId));
    if (screeningIdentifierCollection.length === 0 && this.orderStateService.getItem(storageKey.parentOrder)) {
      screeningIdentifierCollection.push(JSON.parse(this.orderStateService.getItem(storageKey.parentOrder)).parentOrderScreening.id);
    }

    const bookingIdByScreeningId: Map<string, string> = new Map<string, string>();
    this.order.screeningItems.forEach((x) => {
      bookingIdByScreeningId.set(x.screeningId, x.bookingId);
    });

    if (!this.basketPageModel) {
      this.loadBasket((basketPageModel: BasketPageModel) => {
        const screenMap = new Array<ScreenScreeningLinkModel>();
        this.tickets = basketPageModel.tickets;

        const chain: Array<Observable<any>> = screeningIdentifierCollection.map((screeningId) => {
          const findByScreeningOptions: FindByScreeningOptions = {
            disableOccupancy: true
          };

          return this.screenDataProvider.getScreenByAndCheckIfScreeningIsEvent(this.cinemaId, screeningId, findByScreeningOptions).pipe(
            map((x) => {
              return [screeningId, basketPageModel.event ? this.screenDataProvider.makeScreenModelForEvent(x, basketPageModel.event) : x];
            })
          );
        });

        merge(...chain).subscribe({
          next: ([screeningId, screen]) => {
            const screenModel: ScreenViewModel = screen as ScreenViewModel;
            this.screen = screen;
            this.analyticsHelperService.findAnalysisProviderToInformByMovieId(screen.movieId, order);
            screenMap.push(new ScreenScreeningLinkModel(screeningId, bookingIdByScreeningId.get(screeningId), this.order.orderNumber, screenModel));

            if (screenModel.posterUrl === undefined || screenModel.posterUrl === null || screenModel.posterUrl === '') {
              screenModel.posterUrl = makeUrl(this.environment, this.environment.constants.moviePosterPlaceholder);
            }
          },
          error: (e) => {
            this.countdownComponentService.destroy();
            this.orderStateService.removeOrder();
          },
          complete: () => {
            this.screenMap = screenMap;
            const getCatering = this.cateringSaleEnabled
              ? this.orderDataProvider.getCatering(this.cinemaId, this.orderId, this.screen.screenGroupId).pipe(
                  map((cateringCollection) => {
                    this.catering = cateringCollection;
                    return cateringCollection;
                  })
                )
              : of(null);
            const result = forkJoin([this.loadVoucherItemList(), this.loadCateringItemList(), getCatering]);
            result.subscribe({
              complete: () => {
                this.checkAndShowModal(order);
                this.loaderService.hideLoader(LoaderEnum.MAIN);
                this.countdownComponentService.destroy();
                this.orderStateService.removeOrder();
              }
            });
          }
        });
      });
    }
  }

  checkAndShowModal(order: OrderViewModel) {
    if (order.status !== OrderStatus.CANCELLED && this.modal) {
      this.modalRef = this.modalService.show(this.modal, { ignoreBackdropClick: true });
    }
  }

  toogleIsQrCodeZoom(bookingId: string): void {
    if (!this.showZoomIcon) {
      return;
    }

    this.isQrCodeZoom[bookingId] = !this.isQrCodeZoom[bookingId];
  }

  toggleVoucherQrCodeZoom(): void {
    this.isVoucherQrCodeZoom = !this.isVoucherQrCodeZoom;
  }

  /**
   * Loads voucher item list
   */
  private loadVoucherItemList(): Observable<Array<VoucherTypeModel>> {
    this.selectedVoucherList = this.voucherService.createVoucherCollectionFromOrder(this.order);

    return of([]);
  }

  private loadBasket(callback?: any) {
    this.basketDataProvider.updateBasket(this.order, this.cinema.id).subscribe((result: BasketPageModel) => {
      this.basketPageModel = result;
      if (callback) {
        callback(result);
      }
    });
  }

  /**
   * Loads catering item list
   */
  private loadCateringItemList(): Observable<CateringViewModel> {
    if (this.cateringSaleEnabled === false) {
      return of(null);
    }

    return this.orderDataProvider.getCatering(this.cinema.id, this.order.id).pipe(
      tap((catering) => {
        const cateringAggregation: CateringAggregationViewModel = new CateringAggregationBuilder(catering).build();

        const selectedMap: SelectedModifierItemMap = this.orderCateringService.buildSelectedMap(cateringAggregation, this.order.fbItems);

        if (Object.keys(selectedMap).length > 0) {
          const articleCombinationList: Array<CateringAggregationArticleViewModel> = this.selectedCateringArticleCombinationList || [];
          for (const articleId of Object.keys(selectedMap)) {
            for (const mapping of selectedMap[articleId]) {
              const articleCombination: CateringAggregationArticleViewModel = this.cateringService.buildSelectedArticle(
                mapping.article,
                mapping.selectedQuantity,
                mapping.selectedModifierItemMap,
                mapping.selectedSubArticleMap
              );

              articleCombinationList.push(articleCombination);
            }
          }

          this.selectedCateringArticleCombinationList = articleCombinationList.map((x) => x);
        }
      })
    );
  }

  public findTicketById(id: string): TicketViewModel {
    const ticket = this.tickets.filter(function (item) {
      return item.id === id;
    });

    return ticket.length > 0 ? ticket[0] : null;
  }

  public findCateringNameById(id: string): string {
    for (const item of this.catering.groups) {
      if (item.id === id) {
        return item.name;
      }
    }

    for (const item of this.catering.articles) {
      if (item.id === id) {
        return item.name;
      }

      for (const subItem of item.replacementList) {
        if (subItem.id === id) {
          return subItem.name;
        }
      }

      for (const subItem of item.modifierCollectionList) {
        if (subItem.id === id) {
          return subItem.name;
        }
      }

      for (const subItem of item.subArticleList) {
        if (subItem.id === id) {
          return subItem.name;
        }
      }
    }
  }

  public backToScreen(): void {
    this.navigationService.goTo(['screen', this.screenMap[0].screeningId], {
      queryParams: {
        cinemaId: this.cinema.id
      }
    });
  }

  public backToMain(): void {
    this.navigationService.goTo(['screening']);
  }

  public loadingEvent($event) {
    setTimeout(() => {
      this.childLoaderStatus = this.loaderService.getLoaderStatus();
    }, 0);
  }

  onModalClose() {
    this.modalRef.hide();
  }
}
